
import { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { AccountFormNested } from '@/components/forms/AccountFormNested';
import { useAccounts } from '@/contexts/accounts';
import { AppModal } from '@/components/ui/modal';

interface CardAccountFieldProps {
  value: string;
  onChange: (value: string) => void;
  error?: string;
  disabled?: boolean;
  required?: boolean;
}

export const CardAccountField = ({
  value,
  onChange,
  error,
  disabled,
  required = false
}: CardAccountFieldProps) => {
  const [showAccountForm, setShowAccountForm] = useState(false);
  const [lastCreatedAccountName, setLastCreatedAccountName] = useState<string | null>(null);
  const [selectOpen, setSelectOpen] = useState(false);
  const {
    accounts,
    addAccount
  } = useAccounts();
  
  // Auto-select newly created account when accounts update
  useEffect(() => {
    if (lastCreatedAccountName && accounts.length > 0) {
      const newAccount = accounts.find(acc => acc.name === lastCreatedAccountName);
      if (newAccount && newAccount.id !== value) {
        console.log('Auto-selecting newly created account:', newAccount.name);
        onChange(newAccount.id);
        setLastCreatedAccountName(null);
      }
    }
  }, [accounts, lastCreatedAccountName, onChange, value]);
  
  const handleAddAccount = async (accountData: any) => {
    try {
      console.log('Creating account:', accountData.name);
      setLastCreatedAccountName(accountData.name);
      await addAccount(accountData);
      setShowAccountForm(false);
    } catch (error) {
      console.error('Error adding account:', error);
      setLastCreatedAccountName(null);
    }
  };
  
  const handleValueChange = (newValue: string) => {
    // For required field, don't allow "none" selection
    if (required && newValue === 'none') {
      return;
    }
    // Convert "none" back to empty string for the form
    onChange(newValue === 'none' ? '' : newValue);
  };

  // Convert empty string to "none" for the Select component (only if not required)
  const selectValue = value === '' ? required ? '' : 'none' : value;
  const sortedAccounts = [...accounts].sort((a, b) => a.name.localeCompare(b.name));
  
  const selectedAccount = sortedAccounts.find(acc => acc.id === value);
  
  return (
    <div>
      <Label htmlFor="accountId">
        Conta Vinculada {required && <span className="text-destructive ml-1">*</span>}
      </Label>
      
      <Select value={selectValue} onValueChange={handleValueChange} disabled={disabled} open={selectOpen} onOpenChange={setSelectOpen}>
        <SelectTrigger id="accountId" className={`rounded-xl ${error ? 'border-destructive' : ''}`}>
          <SelectValue placeholder="Selecionar conta">
            {selectedAccount && (
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: selectedAccount.color }} />
                <span>{selectedAccount.name} - {selectedAccount.bank}</span>
              </div>
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {!required && <SelectItem value="none">Nenhuma conta vinculada</SelectItem>}
          {sortedAccounts.length === 0 ? (
            <div className="p-2">
              <Button variant="ghost" className="w-full justify-start text-left" onClick={() => { setSelectOpen(false); setShowAccountForm(true); }}>
                <Plus className="h-4 w-4 mr-2" />
                Criar Nova Conta
              </Button>
            </div>
          ) : (
            <>
              {sortedAccounts.map(account => (
                <SelectItem key={account.id} value={account.id}>
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: account.color }} />
                    <span>{account.name} - {account.bank}</span>
                  </div>
                </SelectItem>
              ))}
              <div className="border-t mt-1 pt-1">
                <Button variant="ghost" onClick={() => { setSelectOpen(false); setShowAccountForm(true); }} className="w-full justify-start text-left text-sm">
                  <Plus className="h-4 w-4 mr-2" />
                  Criar Nova Conta
                </Button>
              </div>
            </>
          )}
        </SelectContent>
      </Select>
      
      {error && (
        <p className="text-sm text-destructive mt-1">
          {error}
        </p>
      )}

      <AppModal 
        open={showAccountForm} 
        onOpenChange={(open) => !open && setShowAccountForm(false)}
        variant="form"
        size="lg"
      >
        <AccountFormNested 
          onClose={() => setShowAccountForm(false)} 
          onSubmit={handleAddAccount} 
        />
      </AppModal>
    </div>
  );
};
